import { EditorState } from "draft-js";
import { noop } from "lodash";
import * as React from "react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { ScrollValues } from "react-scrollbars-custom";
import { imageAnnotationsResx } from "~/resxDist/imageAnnotationsResx";
import { VerticalScrollbars } from "~/sharedComponents/scrollbars/VerticalScrollbars";
import {
    getInitialStateFromText,
    getMentions,
    getTeamMentions,
    getText,
    MessageEditor,
    RootMessageBoardState,
    scrollSelectionIntoView,
    useMentionPluginForMessageBoard,
} from "../../index";
import { getProjectId, getProjectOrTask } from "../selectors";
import { AnnotationItemExtended } from "../types";

interface Props {
    onChange: any;
    value: string;
    annotation: AnnotationItemExtended;
    withScroll?: boolean;
}

export const ImageAnnotationMessageContainer: React.FC<Props> = ({ ...props }) => {
    const projectId = useSelector(getProjectId);
    const entity = useSelector(getProjectOrTask);
    const [isFocused, setFocus] = useState(true);

    const { plugin, Mentions } = useMentionPluginForMessageBoard({ projectId, projectOrTask: entity });
    return (
        <>
            <MessageEditorWithScrollbar
                placeholder={imageAnnotationsResx.write_comment}
                plugin={plugin}
                {...props}
                isFocused={isFocused}
                setFocus={setFocus}
            />
            <Mentions isFocused={isFocused} />
        </>
    );
};

type MessageEditorProps = React.ComponentProps<typeof MessageEditor>;
type MessageEditorWrapperProps = Pick<MessageEditorProps, "plugin" | "placeholder"> &
    Props & {
        withScroll?: boolean;
        isFocused: boolean;
        setFocus: (isFocused: boolean) => void;
    };

const MessageEditorWrapper: React.FC<MessageEditorWrapperProps> = ({
    plugin,
    placeholder,
    onChange,
    value,
    annotation,
    setFocus,
    isFocused,
}) => {
    const entity = useSelector(getProjectOrTask);
    const mentions = useSelector((state: any) => getMentions(state, entity));
    const teams = useSelector((state: RootMessageBoardState) => getTeamMentions(state, entity));

    const [state, setState] = useState(() => getInitialStateFromText(value, mentions, teams));

    const { x, y } = annotation.geometry;
    React.useEffect(() => {
        setFocus(true);
    }, [x, y]);

    const onChangeState = React.useCallback((newState: EditorState) => {
        setState(newState);
        const message = getText(newState);
        onChange(message);
    }, []);

    // scroll to the end of message when inline editing is opened for big message
    const hasFocusInState = state.getSelection()?.getHasFocus() ?? false;
    React.useEffect(() => {
        if (hasFocusInState) {
            scrollSelectionIntoView();
        }
    }, [hasFocusInState]);

    return (
        <MessageEditor
            state={state}
            onChange={onChangeState}
            isFocused={isFocused}
            onFocusChange={setFocus}
            post={noop}
            plugin={plugin}
            placeholder={placeholder}
            teams={teams}
            mentions={mentions}
        />
    );
};

const messageScrollbarStyles: React.CSSProperties = {
    minWidth: "100%",
    minHeight: 20,
    maxHeight: 80,
};

const MessageEditorWithScrollbar: React.FC<MessageEditorWrapperProps> = ({ withScroll, ...props }) => {
    if (!withScroll) {
        return <MessageEditorWrapper {...props} />;
    }
    let isTrackYVisible = false;
    const onScrollHandle = (scrollValues: ScrollValues) => {
        const _isTrackYVisible = scrollValues.trackYVisible;
        if (isTrackYVisible !== _isTrackYVisible) {
            isTrackYVisible = _isTrackYVisible;
            if (isTrackYVisible) {
                scrollSelectionIntoView();
            }
        }
    };

    return (
        <VerticalScrollbars
            darkTheme={true}
            translateContentSizesToHolder={true}
            style={messageScrollbarStyles}
            fixBlinking={true}
            onScroll={onScrollHandle}
        >
            <MessageEditorWrapper {...props} />
        </VerticalScrollbars>
    );
};
